import { Card, FormGroup, FormSection } from '@ksyos/design-system';
import { chain, cloneDeep, uniqBy } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import type { IAddCarePathServiceConsumerSettings } from '@common/interfaces/addCarePathServicesSettings';
import type { IAutoSubsequentService, IProviderOrganization } from '@common/interfaces/autoSubsequentService';
import type {
    IOrganization,
    IPractitioner,
    IReactSelectOptions,
    IService,
    IServiceProviderOrganizationsPractitioners,
    ISubsequentService,
} from '@frontend/components/interfaces';
import type { ProviderService } from '@frontend/components/interfaces/careProviderAddRightsState';
import { ServiceType } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { parseNameForSearch } from '@common/utils/parseNameForSearch';
import { DropdownInput } from '@frontend/components/fields';
import { SettingAnswerTypes } from '@frontend/components/pages/carePath/constants/settingsLists';
import { SettingsSearchableDropDown } from '@frontend/components/pages/carePath/modals/components/SettingsSearchableDropDown';
import { SettingsSwitch } from '@frontend/components/pages/carePath/modals/components/SettingsSwitch';
import { translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import type { IServiceSettingsProps } from '../interfaces/serviceSettingsProps';
import { createSettingsToRenderList } from '../utils/createSettingsToRenderList';
import { SharedServiceSettings } from './SharedServiceSettings';

interface IConsumerServiceSettingsProps extends IServiceSettingsProps<IAddCarePathServiceConsumerSettings> {
    employmentStartDate?: string;
    organization?: IOrganization;
    practitioner?: IPractitioner;
    providerServices: ProviderService[];
}

type SucceedingService = Omit<
    IService,
    'code' | 'authorization' | 'customControlVersion' | 'episodeCategory' | 'isActive' | 'isFlex'
>;

export function ConsumerServiceSettings(props: IConsumerServiceSettingsProps) {
    const abortableFetch = useAbortableFetch();
    const [providerOrganizations, setProviderOrganizations] = useState<IProviderOrganization[]>([]);
    const [possibleSubsequentServices, setPossibleSubsequentServices] = useState<ISubsequentService[]>([]);

    useEffect(() => {
        loadSubsequentServices();
    }, [props.service.id]);

    useEffect(() => {
        const succeedingService = getSucceedingService(props.service.settings);

        if (succeedingService) {
            sanitizeProviderOrganization(succeedingService);
            getServiceProviderOrganizations('', succeedingService);
        }
    }, [
        props.service.settings,
        props.providerServices,
        isPractitionerProviderForSubsequentService(getSucceedingService(props.service.settings)),
    ]);

    return (
        <>
            <Card>
                <Card.Header title={props.title} />
                <Card.Body>{renderListItems(props.settingsQuestionList)}</Card.Body>
            </Card>
        </>
    );

    function renderListItems(list: { [key: string]: any }) {
        const currentSettings = cloneDeep(props.service.settings);
        const settingsToRender = createSettingsToRenderList(list, currentSettings, props.service.code);

        return chain(settingsToRender)
            .groupBy((i) => i.header)
            .map((setting, header) => {
                return (
                    <Fragment key={header}>
                        <FormSection title={header}>
                            <>
                                {setting.map(({ listKey }) => {
                                    if (list[listKey].type === SettingAnswerTypes.subsequentServices) {
                                        if (possibleSubsequentServices.length === 0) {
                                            return null;
                                        }

                                        const succeedingService = getSucceedingService(currentSettings);

                                        if (
                                            listKey === 'providerPractitionerId' &&
                                            [ServiceType.referral, ServiceType.referralSmall].some(
                                                (serviceType) => serviceType === succeedingService?.serviceType
                                            )
                                        ) {
                                            return null;
                                        }

                                        return (
                                            <FormGroup
                                                key={currentSettings.id + listKey}
                                                label={list[listKey].label}
                                            >
                                                {listKey === 'addAutoSubsequentServices' && (
                                                    <SettingsSwitch
                                                        listKey={listKey}
                                                        onChange={(value) =>
                                                            handleChangeSubsequentServices(value, listKey)
                                                        }
                                                        value={Boolean(currentSettings.addAutoSubsequentServices)}
                                                    />
                                                )}
                                                {listKey === 'subsequentServiceId' &&
                                                    currentSettings.autoSubsequentService && (
                                                        <DropdownInput
                                                            error={props.errors.get(
                                                                `${props.errorPrefix}.autoSubsequentService.${listKey}`
                                                            )}
                                                            onChange={(value) =>
                                                                handleChangeSubsequentServices(value, listKey)
                                                            }
                                                            placeholder={translateKey('common.clickAndChooseFromList')}
                                                            selectOptions={possibleSubsequentServices.map(
                                                                (subsequentService) => ({
                                                                    value: subsequentService.id,
                                                                    text:
                                                                        subsequentService.succeedingService?.name ?? '',
                                                                })
                                                            )}
                                                            value={
                                                                currentSettings.autoSubsequentService
                                                                    .subsequentServiceId
                                                            }
                                                        />
                                                    )}
                                                {listKey === 'isDeviation' && currentSettings.autoSubsequentService && (
                                                    <SettingsSwitch
                                                        listKey={listKey}
                                                        onChange={(value) =>
                                                            handleChangeSubsequentServices(value, listKey)
                                                        }
                                                        value={Boolean(
                                                            currentSettings.autoSubsequentService.isDeviation
                                                        )}
                                                    />
                                                )}
                                                {listKey === 'providerOrganizationId' &&
                                                    currentSettings.autoSubsequentService?.subsequentServiceId &&
                                                    succeedingService && (
                                                        <SettingsSearchableDropDown
                                                            error={props.errors.get(
                                                                `${props.errorPrefix}.autoSubsequentService.${listKey}`
                                                            )}
                                                            key={`${getProviderOrganizationSelectKey(
                                                                succeedingService
                                                            )}_organization`}
                                                            loadOptions={(input) =>
                                                                getServiceProviderOrganizations(
                                                                    input,
                                                                    succeedingService
                                                                )
                                                            }
                                                            onChange={(value) =>
                                                                handleChangeSubsequentServices(
                                                                    value,
                                                                    listKey,
                                                                    succeedingService
                                                                )
                                                            }
                                                            value={
                                                                currentSettings.autoSubsequentService
                                                                    .providerOrganizationId
                                                            }
                                                        />
                                                    )}
                                                {listKey === 'providerPractitionerId' &&
                                                    currentSettings.autoSubsequentService?.providerOrganizationId &&
                                                    succeedingService && (
                                                        <SettingsSearchableDropDown
                                                            error={props.errors.get(
                                                                `${props.errorPrefix}.autoSubsequentService.${listKey}`
                                                            )}
                                                            key={`${currentSettings.autoSubsequentService?.providerOrganizationId}_practitioner`}
                                                            loadOptions={(input) =>
                                                                getServiceProviderPractitioners(
                                                                    input,
                                                                    succeedingService
                                                                )
                                                            }
                                                            onChange={(value) =>
                                                                handleChangeSubsequentServices(
                                                                    value,
                                                                    listKey,
                                                                    succeedingService
                                                                )
                                                            }
                                                            value={
                                                                currentSettings.autoSubsequentService
                                                                    .providerPractitionerId
                                                            }
                                                        />
                                                    )}
                                            </FormGroup>
                                        );
                                    }

                                    return (
                                        <SharedServiceSettings<IAddCarePathServiceConsumerSettings>
                                            currentSettings={currentSettings}
                                            errorPrefix={props.errorPrefix}
                                            errors={props.errors}
                                            key={`${props.service.id}_${listKey}`}
                                            list={list}
                                            listKey={listKey}
                                            onSettingsChange={handleSettingsChange}
                                            requiredProperties={props.requiredProperties}
                                        />
                                    );
                                })}
                            </>
                        </FormSection>
                    </Fragment>
                );
            })
            .value();
    }

    function handleChangeSubsequentServices(
        value: boolean | string | number | null | IReactSelectOptions,
        listKey: string,
        succeedingService?: SucceedingService
    ) {
        const currentAutoSubsequentService: IAutoSubsequentService = {
            ...props.service.settings.autoSubsequentService,
        };
        const autoSubsequentService = () => {
            switch (listKey) {
                case 'addAutoSubsequentServices': {
                    return value ? { succeedingServiceId: null, isDeviation: false } : null;
                }
                case 'isDeviation': {
                    return {
                        ...currentAutoSubsequentService,
                        isDeviation: value,
                    };
                }
                case 'subsequentServiceId': {
                    return value
                        ? {
                              subsequentServiceId: value,
                              isDeviation: currentAutoSubsequentService?.isDeviation || false,
                              providerOrganizationId: null,
                              providerPractitionerId: null,
                          }
                        : {};
                }
                case 'providerOrganizationId': {
                    return {
                        ...currentAutoSubsequentService,
                        providerOrganizationId: value,
                        providerOrganization: providerOrganizations.find((organization) => organization.id === value),
                        providerPractitionerId: null,
                        succeedingService: { ...succeedingService },
                    };
                }
                case 'providerPractitionerId': {
                    return {
                        ...currentAutoSubsequentService,
                        providerPractitionerId: value,
                    };
                }
                default:
                    return null;
            }
        };

        props.onChange(
            {
                ...props.service.settings,
                ...(listKey === 'addAutoSubsequentServices' && { addAutoSubsequentServices: Boolean(value) }),
                autoSubsequentService: autoSubsequentService() as IAutoSubsequentService,
            },
            `${props.errorPrefix}.${listKey}`
        );
    }

    function getProviderOrganizationSelectKey(succeedingService: SucceedingService) {
        if (
            props.service.settings.autoSubsequentService?.providerOrganizationId === props.organization?.id &&
            isPractitionerProviderForSubsequentService(succeedingService) === false
        ) {
            return 'own';
        }

        return 'other';
    }

    function handleSettingsChange(
        value: boolean | string | number | null,
        listKey: keyof IAddCarePathServiceConsumerSettings
    ) {
        props.onChange({ ...props.service.settings, [listKey]: value }, `${props.errorPrefix}.${listKey}`);
    }

    function getSucceedingService(settings: IAddCarePathServiceConsumerSettings) {
        return possibleSubsequentServices.find(({ id }) => id === settings.autoSubsequentService?.subsequentServiceId)
            ?.succeedingService;
    }

    function isPractitionerProviderForSubsequentService(succeedingService: SucceedingService | undefined) {
        if (!succeedingService) {
            return false;
        }

        return props.providerServices.some(
            (providerService) =>
                providerService.id === succeedingService.id && providerService.settings.isRequestRecipient
        );
    }

    function sanitizeProviderOrganization(succeedingService: SucceedingService) {
        if (
            props.service.settings.autoSubsequentService?.providerOrganizationId === props.organization?.id &&
            isPractitionerProviderForSubsequentService(succeedingService) === false
        ) {
            handleChangeSubsequentServices(null, 'providerOrganizationId', succeedingService);
        }
    }

    async function loadSubsequentServices() {
        const url = `/api/subsequentServices?serviceId=${props.service.id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        setPossibleSubsequentServices(result.json.items);
    }

    async function getServiceProviderOrganizations(searchInput: string, succeedingService: SucceedingService) {
        const serviceProviderOrganizations: IProviderOrganization[] = [];

        if (isPractitionerProviderForSubsequentService(succeedingService) && props.organization) {
            serviceProviderOrganizations.push({
                id: props.organization?.id,
                name: props.organization?.name,
                isRequestRecipient: true,
            });
        }

        const searchText = searchInput ? `&searchText=${encodeURIComponent(parseNameForSearch(searchInput))}` : '';

        const isBiometrician = succeedingService.serviceType === ServiceType.examination ? '&isBiometrician=true' : '';
        const isReferral = succeedingService.serviceType === ServiceType.referral ? '&isReferral=true' : '';
        const isReferralSmall =
            succeedingService.serviceType === ServiceType.referralSmall ? '&isReferralSmall=true' : '';
        const startDate = props.employmentStartDate ? `&startDate=${props.employmentStartDate}` : '';

        const url = `/api/services/${succeedingService.id}/serviceProviders/organizationsAndPractitioners?${isBiometrician}${searchText}${isReferral}${isReferralSmall}${startDate}`;

        const result = await abortableFetch.fetchJson(url, 'GET');
        const items = result.json.items.map(
            ({ id, name, isOrganizationRequestRecipient }: IServiceProviderOrganizationsPractitioners) => ({
                id,
                name,
                isRequestRecipient: isOrganizationRequestRecipient,
            })
        );
        serviceProviderOrganizations.push(...items);

        setProviderOrganizations(uniqBy(serviceProviderOrganizations, 'id'));

        return uniqBy(serviceProviderOrganizations, 'id').map((organization) => ({
            value: organization.id,
            label: organization.name,
        }));
    }

    async function getServiceProviderPractitioners(searchText: string, succeedingService: SucceedingService) {
        const selectedProviderOrganization = props.service.settings?.autoSubsequentService?.providerOrganizationId;

        if (!selectedProviderOrganization) {
            return [];
        }

        let url = `/api/serviceProvidersList/${
            succeedingService.id
        }/practitioners?isRequestRecipient=true&organizationId=${selectedProviderOrganization}${
            searchText ? `&searchText=${searchText}` : ''
        }`;
        if (succeedingService.serviceType === ServiceType.examination) {
            url += '&isBiometrician=true';
        }

        const practitioners = (await abortableFetch.fetchJson(url, 'GET')).json.items as IPractitioner[];

        if (selectedProviderOrganization === props.organization?.id && props.practitioner) {
            practitioners.push(props.practitioner);
        }

        return practitioners.map((practitioner) => ({
            value: practitioner.id,
            label: getDisplayName(practitioner),
        }));
    }
}
